"use client";

import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "next/navigation";
import { setCustomPageTitle } from "@/lib/page-titles";
import { CodeModeSection } from "./CodeModeSection";
import { cn, isLoginEnabled } from "@/lib/utils";
import { DecodeText } from "@/components/ui/decode-text";
import { ScrollRevealText } from "@/components/ui/scroll-reveal-text";
import { PillButton } from "@/components/ui/pill-button";
import { ShowcaseCard } from "@/components/ui/showcase-card";
import Link from "next/link";
import { LandingFooter } from "@/components/ui/footer";
import { SiteNavigation } from "@/components/ui/site-navigation";
import { motion, AnimatePresence } from "framer-motion";

export function AgenticaPageContent() {
  const isDark = true;
  const searchParams = useSearchParams();
  const useSansFont = searchParams.get("font") !== "mix";
  const loginEnabled = isLoginEnabled();

  useEffect(() => {
    setCustomPageTitle("Agentica by Symbolica");
  }, []);

  return (
    <main
      className={cn(
        "grain relative min-h-screen bg-[#09090a] text-white font-arizona-sans antialiased",
        useSansFont && "use-arizona-sans"
      )}
    >
      <SiteNavigation isDark={isDark} activePage="agentica-sdk" />

      {/* Hero — full viewport on desktop (unchanged), stacked on mobile */}
      {/* Desktop hero: absolute code demo + gradient overlay + text at bottom */}
      <section className="relative hidden md:flex h-screen max-h-[750px] flex-col justify-end px-6 sm:px-10 pb-12 pt-32 overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 bottom-[35%] lg:bottom-0 z-0 flex items-start justify-center px-6 sm:px-10 overflow-hidden"
          style={{ paddingTop: "111px" }}
        >
          {/* Outer glow */}
          <div
            className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[90%] h-[500px] pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(118,120,227,0.18) 0%, rgba(118,120,227,0.06) 50%, transparent 75%)",
              filter: "blur(70px)",
            }}
          />
          {/* Scan-lines on glow */}
          <div
            className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[90%] h-[500px] pointer-events-none"
            style={{
              background:
                "repeating-linear-gradient(to bottom, transparent, transparent 1px, rgba(118,120,227,0.14) 1px, rgba(118,120,227,0.14) 2px)",
              maskImage:
                "radial-gradient(ellipse at center, black 25%, transparent 70%)",
              WebkitMaskImage:
                "radial-gradient(ellipse at center, black 25%, transparent 70%)",
            }}
          />
          <HeroCodeWrapper />
        </div>
        <div
          className="absolute bottom-0 left-0 right-0 h-[55%] lg:h-[50%] pointer-events-none z-[1]"
          style={{
            background:
              "linear-gradient(to top, #09090a 0%, #09090a 30%, rgba(9,9,10,0.85) 55%, rgba(9,9,10,0.3) 75%, transparent 100%)",
          }}
        />
        <div
          className="absolute bottom-0 left-0 right-0 h-[60%] pointer-events-none z-[1]"
          style={{
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            maskImage:
              "linear-gradient(to top, black 0%, black 30%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(to top, black 0%, black 30%, transparent 100%)",
          }}
        />
        <div
          className="relative z-10 max-w-7xl mx-auto w-full"
          style={{ transform: "translateY(48px)" }}
        >
          <h1 className="text-4xl sm:text-[3rem] font-normal tracking-[-0.03em] leading-[1.05] mb-4 max-w-3xl">
            Beyond Code Mode: The Agentica SDK
          </h1>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-10 mb-12">
            <div className="max-w-md">
              <p className="text-base font-light text-white/50 leading-relaxed">
                Build agents that interact with your runtime objects by writing
                code. State of the art performance, securely sandboxed.
              </p>
            </div>
            <Link
              href={
                loginEnabled
                  ? "/login?tab=signup"
                  : "/blog/beyond-code-mode-agentica"
              }
            >
              <PillButton variant="primary" responsive className="shrink-0">
                {loginEnabled ? "Get Started Now" : "Read the Blog"}
              </PillButton>
            </Link>
          </div>
        </div>
      </section>

      {/* Mobile hero: text + button centered, code component left-aligned */}
      <section className="md:hidden flex flex-col items-center text-center pt-24 pb-12 px-6 overflow-hidden">
        <h1 className="text-3xl font-normal tracking-[-0.03em] leading-[1.05] mb-4">
          Beyond Code Mode: The Agentica SDK
        </h1>
        <p className="text-sm font-light text-white/50 leading-relaxed max-w-md mb-6">
          Build agents that interact with your runtime objects by writing code.
          State of the art performance, securely sandboxed.
        </p>
        <Link
          href={
            loginEnabled
              ? "/login?tab=signup"
              : "/blog/beyond-code-mode-agentica"
          }
        >
          <PillButton variant="primary" responsive className="mb-10">
            {loginEnabled ? "Get Started Now" : "Read the Blog"}
          </PillButton>
        </Link>
        <div className="w-full text-left">
          <HeroCodeWrapper />
        </div>
      </section>

      {/* About — sticky headline + scrollable content (replaces AlgoBot demo) */}
      <AboutSection loginEnabled={loginEnabled} />

      {/* Code Mode Section */}
      <CodeModeSection />

      {/* Framework Section — open-source + Symbolica platform */}
      <section className="px-6 sm:px-10 border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto">
          <FrameworkSectionAlt />
        </div>
      </section>

      {/* CTA Section - hidden when login is disabled */}
      {loginEnabled && <CTASection />}

      {/* Pricing Section */}
      <PricingSection />

      {/* Scroll Reveal Section - Agents as Code */}
      <AgenticaRevealSection loginEnabled={loginEnabled} />

      <LandingFooter colorMode={isDark ? "dark" : "light"} noMargin />
    </main>
  );
}

/* AboutSection — sticky headline + scrollable video/image placeholders (matches reference) */
function AboutSection({ loginEnabled }: { loginEnabled: boolean }) {
  const items: import("@/components/ui/showcase-card").ShowcaseCardProps[] = [
    {
      num: "01",
      title: "ARC-AGI-2 & 3 with the Agentica SDK",
      desc: "Our open-source ARC-AGI-3 implementation solves all 3 publicly available pre-release games and our ARC-AGI-2 implementation achieves a score of 85.28% with Opus 4.6 (120k) High, increasing the scores of GPT 5.2 (XHigh) and Opus 4.5 by 10 and 20 percentage points respectively. ",
      blogHref: "/blog/arcgentica",
      videoAssetKey: "agentica-sdk/arc3",
      githubLinks: [
        { label: "ARC-2", href: "https://github.com/symbolica-ai/arcgentica" },
        {
          label: "ARC-3",
          href: "https://github.com/symbolica-ai/ARC-AGI-3-Agents",
        },
      ],
    },
    {
      num: "02",
      title: "Runtime as Context: How Agentica Reasons Over Data",
      desc: "What changes when an agent has access to a persistent Python runtime instead of reconstructing context from prior tool outputs? To make this distinction clear, we built a data analysis demo comparing a traditional tool-calling agent built with LangChain\u2019s Deep Agents, and Agentica, which reasons inside a Python runtime (REPL) where intermediate objects are retained across turns.",
      blogHref: "/blog/beyond-code-mode-agentica",
      videoAssetKey: "agentica-sdk/runtime-as-context",
    },
    {
      num: "03",
      title: "Watch a multi-agent system, built with Agentica",
      desc: 'Query comes in: "Backtest a TSLA strategy." Agents jump into action fetching data, running calculations, generating charts seamlessly. Agentica\u2019s types, composition, and dynamic code execution let tasks flow like one mind, orchestrating workflows in real time.',
      videoAssetKey: "agentica-sdk/algobot",
    },
  ];

  return (
    <section className="px-6 sm:px-10 py-32">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-16 lg:gap-20">
          {/* Left column — sticky */}
          <div className="lg:w-1/3 lg:sticky lg:top-32 lg:self-start">
            <h2 className="text-3xl sm:text-4xl font-normal tracking-[-0.03em] leading-[1.1] mb-8">
              Build Code Mode Agents with Any Model
            </h2>
            <p className="text-sm font-light text-white/40 leading-relaxed mb-8">
              A Python and TypeScript SDK for building code mode agents that
              have access to your runtime objects. Deploy on the Symbolica
              Platform.
            </p>
            <Link
              href={
                loginEnabled
                  ? "/login?tab=signup"
                  : "/blog/beyond-code-mode-agentica"
              }
            >
              <PillButton variant="primary" responsive>
                Get Started
              </PillButton>
            </Link>
          </div>

          {/* Right column — scrollable cards with placeholders */}
          <div className="lg:w-2/3 flex flex-col gap-24">
            {items.map((item) => (
              <ShowcaseCard key={item.num} {...item} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* Wrapper that adds 3D perspective tilt on desktop */
function HeroCodeWrapper() {
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    setIsDesktop(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return (
    <div
      className="relative w-full max-w-[1230px] rounded-2xl"
      style={
        isDesktop
          ? {
              transform:
                "perspective(1200px) rotateX(4.6deg) rotateY(-3.45deg)",
              transformOrigin: "center top",
              boxShadow:
                "0 0 80px 20px rgba(118,120,227,0.08), 0 0 160px 60px rgba(118,120,227,0.04)",
            }
          : undefined
      }
    >
      <HeroCodeSection />
      {/* Grainy scan-line overlay on component glow */}
      {isDesktop && (
        <div
          className="absolute -inset-16 pointer-events-none rounded-2xl opacity-[0.06]"
          style={{
            backgroundImage: `
              repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(118,120,227,0.25) 2px, rgba(118,120,227,0.25) 2.5px),
              url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.8' numOctaves='5' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")
            `,
            backgroundSize: "auto, 256px 256px",
            maskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 70%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 70%)",
          }}
        />
      )}
    </div>
  );
}

function HeroCodeSection() {
  const [language, setLanguage] = useState<"python" | "typescript">("python");
  const [highlightStep, setHighlightStep] = useState(0);
  const [logsTypedText, setLogsTypedText] = useState("");
  const [logsPhase, setLogsPhase] = useState<"typing" | "running" | "result">(
    "typing"
  );
  const [hasShownLogs, setHasShownLogs] = useState(false);
  const [isInView, setIsInView] = useState(true);
  const codeBlockRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Track if hero is in viewport (for pausing log typing on mobile)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsInView(entry.isIntersecting);
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Full agent response text to type out
  const agentResponseText = `Let me check!

\`\`\`python
keys = await client.keys.fetch_all()
usages = await asyncio.gather(*[k.usage() for k in keys])
return sum(usages)
\`\`\``;

  // Mark logs as shown when we reach step 2 for the first time
  useEffect(() => {
    if (highlightStep === 2 && !hasShownLogs) {
      setHasShownLogs(true);
    }
  }, [highlightStep, hasShownLogs]);

  // Typewriter effect for logs - only runs once when first shown, pauses when out of view
  const charIndexRef = useRef(0);
  const typeIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (highlightStep !== 2) {
      return;
    }

    // Don't re-run typewriter if we've already completed it
    if (logsPhase === "result") {
      return;
    }

    // If not in view, pause typing
    if (!isInView) {
      if (typeIntervalRef.current) {
        clearInterval(typeIntervalRef.current);
        typeIntervalRef.current = null;
      }
      return;
    }

    // Start or resume typing
    if (charIndexRef.current === 0) {
      setLogsTypedText("");
      setLogsPhase("typing");
    }

    typeIntervalRef.current = setInterval(() => {
      if (charIndexRef.current < agentResponseText.length) {
        charIndexRef.current++;
        setLogsTypedText(agentResponseText.slice(0, charIndexRef.current));
      } else {
        if (typeIntervalRef.current) {
          clearInterval(typeIntervalRef.current);
          typeIntervalRef.current = null;
        }
        setLogsPhase("running");
        // After 1 second of "running", show result
        setTimeout(() => {
          setLogsPhase("result");
        }, 1000);
      }
    }, 20); // 20ms per character

    return () => {
      if (typeIntervalRef.current) {
        clearInterval(typeIntervalRef.current);
        typeIntervalRef.current = null;
      }
    };
  }, [highlightStep, isInView]);

  // Cycle through highlights every 3 seconds - pause when not in view
  useEffect(() => {
    if (!isInView) return;
    const interval = setInterval(() => {
      setHighlightStep((prev) => (prev + 1) % 3);
    }, 3000);
    return () => clearInterval(interval);
  }, [isInView]);

  const pythonCode = `# 3rd party API for fetching market data
from third_party_library import Client
from agentica import spawn

# Pass the client to the agent
data_agent = await spawn(
    scope={'client': Client()}
)

# Ask it a question and print the result
print(await data_agent.call(
    float, "How much have I spent this month?"
))`;

  const typescriptCode = `// 3rd party API for fetching market data
import {Client} from "third_party_library";
import {spawn} from "agentica";

// Pass the client to the agent
const dataAgent = await spawn({
    scope: {client: new Client()}
});

// Ask it a question and log the result
console.log(await dataAgent.call<number>(
    "How much have I spent this month?"
));`;

  const codeContent = language === "python" ? pythonCode : typescriptCode;

  return (
    <div className="relative w-full">
      <div className="relative">
        {/* Content area - Code examples or Chat */}
        <div
          className="flex flex-col items-center relative w-full"
          ref={containerRef}
        >
          {/* Responsive container - full width on mobile, max-w-[880px] on desktop */}
          <div className="border border-white/[0.08] rounded-2xl bg-[#09090a] overflow-hidden flex flex-col md:flex-row w-full">
            {/* Left side - Code or Chat with its own title bar */}
            <div className="flex-1 flex flex-col min-w-0">
              {/* Title bar with traffic lights - always visible */}
              <div className="flex items-center justify-between gap-2 px-4 py-3 shrink-0">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500/80" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                  <div className="w-3 h-3 rounded-full bg-green-500/80" />
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={language}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.15 }}
                      className="font-mono text-xs text-white/70 ml-2"
                    >
                      {language === "python" ? "agents.py" : "agents.ts"}
                    </motion.span>
                  </AnimatePresence>
                </div>
                {/* Language Toggle */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setLanguage("python")}
                    className={`font-mono text-[10px] px-2 py-0.5 rounded transition-all cursor-pointer ${
                      language === "python"
                        ? "bg-white/10 text-white"
                        : "text-white/40 hover:bg-white/5 hover:text-white/70"
                    }`}
                  >
                    Python
                  </button>
                  <button
                    onClick={() => setLanguage("typescript")}
                    className={`font-mono text-[10px] px-2 py-0.5 rounded transition-all cursor-pointer ${
                      language === "typescript"
                        ? "bg-white/10 text-white"
                        : "text-white/40 hover:bg-white/5 hover:text-white/70"
                    }`}
                  >
                    TypeScript
                  </button>
                </div>
              </div>

              {/* Content area - Code examples */}
              <div className="flex-1 relative overflow-hidden min-h-[320px] md:min-h-[380px]">
                <div className="p-6 pt-2" ref={codeBlockRef}>
                  <pre className="font-mono text-[11px] sm:text-sm leading-relaxed overflow-x-auto">
                    <code>
                      {codeContent.split("\n").map((line, i) => {
                        const isComment =
                          language === "python"
                            ? line.trim().startsWith("#")
                            : line.trim().startsWith("//");
                        const isEmptyLine = line.trim() === "";

                        // Helper to highlight strings
                        const highlightStrings = (text: string) => {
                          const parts = text.split(/("[^"]*"|'[^']*')/g);
                          return parts.map((part, idx) => {
                            if (
                              (part.startsWith('"') && part.endsWith('"')) ||
                              (part.startsWith("'") && part.endsWith("'"))
                            ) {
                              return (
                                <span key={idx} className="text-[#ce9178]">
                                  {part}
                                </span>
                              );
                            }
                            const withKeywords = part
                              .replace(
                                /\b(lambda|True|False|None|float|const|new|number)\b/g,
                                "{{KW:$1}}"
                              )
                              .split(/({{KW:[^}]+}})/g);
                            return withKeywords.map((segment, sidx) => {
                              const kwMatch = segment.match(/{{KW:([^}]+)}}/);
                              if (kwMatch) {
                                return (
                                  <span
                                    key={`${idx}-${sidx}`}
                                    className="text-[#569cd6]"
                                  >
                                    {kwMatch[1]}
                                  </span>
                                );
                              }
                              return (
                                <span key={`${idx}-${sidx}`}>{segment}</span>
                              );
                            });
                          });
                        };

                        // Line detection for highlighting
                        const isLine0 =
                          language === "python"
                            ? line.includes(
                                "from third_party_library import Client"
                              )
                            : line.includes(
                                'import {Client} from "third_party_library"'
                              );
                        const isLine1 =
                          language === "python"
                            ? line.includes("scope={'client': Client()}")
                            : line.includes("scope: {client: new Client()}");
                        const isLine2 =
                          language === "python"
                            ? line.includes("print(await data_agent.call")
                            : line.includes("console.log(await dataAgent.call");

                        const shouldHighlight =
                          (highlightStep === 0 && isLine0) ||
                          (highlightStep === 1 && isLine1) ||
                          (highlightStep === 2 && isLine2);

                        // Get the label for the highlighted line
                        const getHighlightLabel = () => {
                          if (isLine0 && highlightStep === 0)
                            return "1. Get any runtime object";
                          if (isLine1 && highlightStep === 1)
                            return "2. Pass it to the agent";
                          if (isLine2 && highlightStep === 2)
                            return "3. Run the agent with it";
                          return null;
                        };
                        const highlightLabel = getHighlightLabel();

                        return (
                          <div
                            key={i}
                            className={isComment || isEmptyLine ? "" : "mb-1"}
                          >
                            <div
                              className={`${
                                shouldHighlight
                                  ? `bg-periwinkle/20 pl-1 py-1 transition-all duration-300 flex items-center justify-between ${
                                      highlightLabel
                                        ? "rounded-t-md lg:rounded-b-md pr-0 lg:pr-0"
                                        : "px-1 rounded-md"
                                    }`
                                  : "transition-all duration-300"
                              }`}
                            >
                              {isComment ? (
                                <span className="text-white/40 flex-shrink-0">
                                  {line}
                                </span>
                              ) : isEmptyLine ? (
                                <span>&nbsp;</span>
                              ) : line.startsWith("from ") ? (
                                <span className="flex-shrink-0">
                                  <span className="text-[#c586c0]">from </span>
                                  <span className="text-[#9cdcfe]">
                                    {
                                      line
                                        .split("from ")[1]
                                        ?.split(" import")[0]
                                    }
                                  </span>
                                  <span className="text-[#c586c0]">
                                    {" "}
                                    import{" "}
                                  </span>
                                  <span className="text-[#9cdcfe]">
                                    {line.split(" import ")[1]}
                                  </span>
                                </span>
                              ) : line.startsWith("import ") ? (
                                <span className="flex-shrink-0">
                                  <span className="text-[#c586c0]">
                                    import{" "}
                                  </span>
                                  <span className="text-[#9cdcfe]">
                                    {line.split("import ")[1]}
                                  </span>
                                </span>
                              ) : line.includes("await ") ? (
                                <span className="flex-shrink-0">
                                  <span className="text-white">
                                    {highlightStrings(
                                      line.split("await ")[0] || ""
                                    )}
                                  </span>
                                  <span className="text-[#c586c0]">await </span>
                                  <span className="text-white">
                                    {highlightStrings(
                                      line.split("await ")[1] || ""
                                    )}
                                  </span>
                                </span>
                              ) : line.includes("=") && !line.includes("==") ? (
                                <span className="flex-shrink-0">
                                  <span className="text-[#9cdcfe]">
                                    {line.split("=")[0]}
                                  </span>
                                  <span className="text-white">
                                    ={" "}
                                    {highlightStrings(
                                      line.split("=").slice(1).join("=")
                                    )}
                                  </span>
                                </span>
                              ) : (
                                <span className="text-white flex-shrink-0">
                                  {highlightStrings(line)}
                                </span>
                              )}
                              {/* Desktop: inline label (LG and above) */}
                              {highlightLabel && (
                                <span className="hidden lg:inline-flex items-center ml-auto bg-periwinkle/30 backdrop-blur-sm text-white text-sm font-arizona-sans whitespace-nowrap px-3 py-1.5 rounded-tr-md rounded-br-md self-stretch -my-1 select-none">
                                  {highlightLabel}
                                </span>
                              )}
                            </div>
                            {/* Mobile/Tablet: label below (below LG) */}
                            {highlightLabel && (
                              <div className="lg:hidden bg-periwinkle/30 backdrop-blur-sm text-white text-sm font-arizona-sans px-3 py-1.5 rounded-b-md select-none">
                                {highlightLabel}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </code>
                  </pre>
                </div>
              </div>
            </div>

            {/* Logs Panel - hidden on mobile, right side on desktop, stays visible once shown */}
            <AnimatePresence>
              {hasShownLogs && (
                <motion.div
                  initial={{ height: 0, width: "100%", opacity: 0 }}
                  animate={{ height: "auto", width: "100%", opacity: 1 }}
                  exit={{ height: 0, width: "100%", opacity: 0 }}
                  transition={{ duration: 0.25, ease: "easeInOut" }}
                  className="hidden md:block shrink-0 md:border-l border-white/[0.06] bg-[#09090a] overflow-hidden md:!w-[300px] md:!h-auto"
                >
                  {/* Title bar */}
                  <div className="flex items-center px-4 py-3">
                    <span className="font-ibm-plex-mono text-[10px] text-white/20 uppercase tracking-wider">
                      Logs
                    </span>
                  </div>

                  {/* Logs content */}
                  <div className="px-4 pb-4 font-mono text-xs overflow-y-auto max-h-[300px] select-none">
                    {/* User message - appears immediately */}
                    <div className="mb-3">
                      <span className="text-white/50">User: </span>
                      <span className="text-white/70">
                        How much have I spent this month?
                      </span>
                    </div>

                    {/* Agent message - types out */}
                    <div className="mb-2">
                      <span className="text-white/50">Agent: </span>
                      <span className="text-white/70 whitespace-pre-wrap">
                        {logsTypedText.split("```python")[0]}
                      </span>
                      {logsTypedText.includes("```python") && (
                        <div className="border border-white/20 rounded overflow-hidden">
                          {/* Python label */}
                          <div className="px-2 py-1 border-b border-white/10">
                            <span className="text-[9px] text-white/30">
                              python
                            </span>
                          </div>
                          {/* Code with syntax highlighting */}
                          <pre className="p-2 text-[10px] overflow-x-auto">
                            <code>
                              {(
                                logsTypedText
                                  .split("```python")[1]
                                  ?.split("```")[0] || ""
                              )
                                .trim()
                                .split("\n")
                                .map((line, i) => (
                                  <div key={i}>
                                    {line
                                      .split(
                                        /(\bawait\b|\breturn\b|\bfor\b|\bin\b)/g
                                      )
                                      .map((part, j) => {
                                        if (
                                          [
                                            "await",
                                            "return",
                                            "for",
                                            "in",
                                          ].includes(part)
                                        ) {
                                          return (
                                            <span
                                              key={j}
                                              className="text-[#c586c0]"
                                            >
                                              {part}
                                            </span>
                                          );
                                        }
                                        // Highlight function calls
                                        const withFuncs =
                                          part.split(/(\w+\()/g);
                                        return withFuncs.map((seg, k) => {
                                          if (seg.endsWith("(")) {
                                            return (
                                              <span
                                                key={`${j}-${k}`}
                                                className="text-[#dcdcaa]"
                                              >
                                                {seg}
                                              </span>
                                            );
                                          }
                                          return (
                                            <span
                                              key={`${j}-${k}`}
                                              className="text-white/60"
                                            >
                                              {seg}
                                            </span>
                                          );
                                        });
                                      })}
                                  </div>
                                ))}
                            </code>
                          </pre>
                        </div>
                      )}
                    </div>

                    {/* Running indicator or Result */}
                    {logsPhase === "running" && (
                      <div className="text-white/50 animate-pulse">
                        running...
                      </div>
                    )}
                    {logsPhase === "result" && (
                      <div className="-mx-4 px-4 py-1.5 mt-4 bg-periwinkle/10 flex items-center gap-1">
                        <span className="text-white/40">Result:</span>
                        <span className="text-periwinkle font-medium">
                          2847.53
                        </span>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

function AgenticaRevealSection({ loginEnabled }: { loginEnabled: boolean }) {
  const firstParagraph = `Agentica, a new agent framework by Symbolica, is built on the premise that code is the most expressive interface through which models can interact with their environment.`;
  const secondParagraph = `Agents can interact with your runtime objects in a sandboxed execution environment. This includes the framework itself, allowing them to delegate capabilities, not just tasks, to other agents.`;

  return (
    <section className="relative bg-[#09090a] border-t border-b border-white/[0.06] scroll-mt-20">
      <div className="mx-auto max-w-7xl px-6 sm:px-10 py-32">
        <div className="font-ibm-plex-mono text-[13px] font-normal uppercase tracking-[0.2em] text-white/40 mb-10">
          <DecodeText text="BEYOND CODE MODE" delay={200} />
        </div>
        <ScrollRevealText
          paragraphs={[firstParagraph, secondParagraph]}
          className="max-w-4xl text-white"
        />
        {loginEnabled && (
          <div className="mt-12">
            <Link href="/login?tab=signup">
              <PillButton variant="primary" responsive>
                Go Beyond Code Mode
              </PillButton>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

function PricingSection() {
  return (
    <section
      id="pricing"
      className="relative border-t border-white/[0.06] scroll-mt-24"
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-10 py-32">
        <div className="flex flex-col md:flex-row gap-12 md:gap-16 md:items-center">
          {/* Left side - Lockup */}
          <div className="flex-1">
            <div className="font-ibm-plex-mono text-[11px] font-normal uppercase tracking-[0.2em] text-white/40 mb-6">
              <DecodeText text="PRICING" delay={200} />
            </div>
            <h2 className="text-3xl sm:text-4xl font-normal tracking-[-0.03em] leading-[1.1] text-white text-pretty">
              Powerful agents.
              <br />
              Simple pricing.
            </h2>
            <p className="font-arizona-sans font-light text-lg text-white/50 mt-6 leading-relaxed text-pretty">
              We charge a 5% fee on top of our underlying inference costs.
              <br />
              See{" "}
              <a
                href="https://openrouter.ai/models?order=top-weekly"
                target="_blank"
                rel="noopener noreferrer"
                className="underline decoration-white/50 hover:opacity-80"
              >
                OpenRouter
              </a>{" "}
              for exact pricing.
            </p>
          </div>

          {/* Right side - Pricing cards as two columns */}
          <div className="w-full md:w-1/2 flex justify-center md:justify-start">
            <div className="w-full flex flex-col md:flex-row gap-6">
              {/* Left Card: Pricing */}
              <div className="flex-1 bg-[#161616] border border-white/15 p-6 md:p-8 flex flex-col items-start">
                <p className="text-[72px] md:text-[96px] font-bold text-white leading-none tracking-tight">
                  5
                  <span className="font-light text-white/50 text-[54px] md:text-[72px]">
                    %
                  </span>
                </p>
                <p className="font-arizona-sans font-light text-lg text-white/50 mt-2">
                  of model inference cost
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="relative bg-[#0d0d0d] border-t border-b border-white/[0.06]">
      <div className="mx-auto max-w-7xl px-6 sm:px-10 py-32">
        <div className="flex flex-col md:flex-row items-start justify-between gap-4 md:gap-16">
          {/* Left side - Header */}
          <div className="flex-1">
            <h2 className="text-3xl sm:text-4xl font-normal tracking-[-0.03em] leading-[1.1] text-white">
              Get started with Agentica.
              <br />
              In minutes.
            </h2>
          </div>

          {/* Right side - Description and Button */}
          <div className="flex-1 flex flex-col items-start">
            <p className="font-arizona-sans font-light text-lg text-white/50 mb-6 leading-relaxed">
              Build multi-agent systems that can execute in your runtime today.
            </p>
            <Link href="/login?tab=signup">
              <PillButton variant="primary" responsive>
                Get started
              </PillButton>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function TopDarkGrid() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-[#050505]" />
      <div className="absolute inset-0 opacity-[0.1]">
        <DarkGridOverlay
          opacity={0.1}
          lineTone="rgba(255,255,255,0.12)"
          size={72}
        />
      </div>
    </div>
  );
}

function DarkGridOverlay({
  opacity = 0.2,
  lineTone = "rgba(255,255,255,0.08)",
  size = 64,
}: {
  opacity?: number;
  lineTone?: string;
  size?: number;
}) {
  return (
    <div
      className="size-full"
      style={{
        backgroundImage: `linear-gradient(${lineTone} 1px, transparent 1px), linear-gradient(90deg, ${lineTone} 1px, transparent 1px)`,
        backgroundSize: `${size}px ${size}px`,
        opacity,
      }}
    />
  );
}

function FrameworkSectionAlt() {
  const [activeTab, setActiveTab] = useState<"agentica" | "symbolica">(
    "agentica"
  );
  const [hoveredTab, setHoveredTab] = useState<"agentica" | "symbolica" | null>(
    null
  );
  const frameworkRef = useRef<HTMLDivElement>(null);

  // Desktop: scroll-driven tab swap — second half shows Symbolica on top
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const handleScroll = () => {
      const el = frameworkRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const midpoint = rect.top + rect.height / 2;
      setActiveTab(
        midpoint < window.innerHeight / 2 ? "symbolica" : "agentica"
      );
    };
    const setup = () => {
      if (mq.matches) {
        window.addEventListener("scroll", handleScroll, { passive: true });
        handleScroll();
      } else {
        window.removeEventListener("scroll", handleScroll);
      }
    };
    setup();
    mq.addEventListener("change", setup);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      mq.removeEventListener("change", setup);
    };
  }, []);

  const layers = [
    {
      id: "agentica",
      number: "01",
      label: "agentica",
      title: "Open-source framework",
      description: (
        <>
          <p className="mb-4">
            Agentica is open-source. Run locally or on the Symbolica platform.
          </p>
          <a
            href="https://github.com/symbolica-ai/agentica-python-sdk"
            target="_blank"
            rel="noopener noreferrer"
          >
            <PillButton variant="primary" responsive icon="github">
              Agentica SDK
            </PillButton>
          </a>
        </>
      ),
    },
    {
      id: "symbolica",
      number: "02",
      label: "symbolica platform",
      title: "Symbolica platform",
      description: (
        <>
          <p className="mb-4">
            The Symbolica platform enables enterprise-scale agentic workloads
            today. Seamlessly integrate and manage multi-agent workloads with
            production applications.
          </p>
          <p>
            Security, permissioning, telemetry and observability. Every agent
            action is observable and traceable in real-time.
          </p>
        </>
      ),
    },
  ];

  return (
    <div className="relative w-full pt-28 md:pt-28 pb-28 md:pb-28">
      {/* Header */}
      <div className="mb-6">
        <div className="font-ibm-plex-mono mb-4 text-[11px] font-normal uppercase tracking-[0.2em] text-white/40">
          <DecodeText text="BUILT FOR THE FUTURE" delay={200} />
        </div>
        <h2 className="text-3xl sm:text-4xl font-normal tracking-[-0.03em] leading-[1.1] text-white">
          Open-source framework, designed for the Symbolica platform
        </h2>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Isometric Diagram (Left Side) */}
        <div className="relative h-[400px] w-full flex-shrink-0 overflow-hidden rounded-lg border border-white/[0.06] lg:h-[500px] lg:flex-1">
          {/* Grid Pattern Background — sparse grid */}
          <div className="absolute inset-0 opacity-25">
            <svg
              className="h-full w-full"
              preserveAspectRatio="none"
              viewBox="0 0 800 686"
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <line
                  key={`h-${i}`}
                  x1="0"
                  y1={i * 137}
                  x2="800"
                  y2={i * 137}
                  stroke="rgba(255,255,255,0.2)"
                  strokeWidth="0.5"
                />
              ))}
              {Array.from({ length: 7 }).map((_, i) => (
                <line
                  key={`v-${i}`}
                  x1={i * 133}
                  y1="0"
                  x2={i * 133}
                  y2="686"
                  stroke="rgba(255,255,255,0.2)"
                  strokeWidth="0.5"
                />
              ))}
            </svg>
          </div>
          {/* Grain overlay */}
          <div
            className="absolute inset-0 z-[2] pointer-events-none opacity-[0.12] mix-blend-overlay"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
              backgroundRepeat: "repeat",
              backgroundSize: "180px",
            }}
          />

          {/* 3D Isometric Layers Container */}
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ perspective: "1000px" }}
          >
            <div className="relative h-[400px] w-[500px] scale-[0.5] md:scale-[0.7] lg:scale-[0.85]">
              {/* Bottom Layer - Symbolica */}
              <div
                className="absolute h-[300px] w-[450px] rounded-lg border backdrop-blur-sm transition-all duration-500 overflow-hidden"
                style={{
                  transform:
                    activeTab === "symbolica"
                      ? "rotateX(60deg) rotateZ(-45deg) scale(1.03)"
                      : "rotateX(60deg) rotateZ(-45deg)",
                  transformStyle: "preserve-3d",
                  left: "50%",
                  top: "60%",
                  marginLeft: "-225px",
                  marginTop: "-150px",
                  borderColor:
                    activeTab === "symbolica"
                      ? "rgba(255,255,255,0.25)"
                      : "rgba(255,255,255,0.08)",
                  boxShadow:
                    activeTab === "symbolica"
                      ? "0 8px 40px rgba(0,0,0,0.6), 0 0 1px rgba(255,255,255,0.1)"
                      : "0 4px 20px rgba(0,0,0,0.4)",
                  background:
                    activeTab === "symbolica"
                      ? "linear-gradient(145deg, rgba(30,30,35,0.95) 0%, rgba(15,15,18,0.95) 100%)"
                      : "linear-gradient(145deg, rgba(22,22,26,0.9) 0%, rgba(10,10,12,0.9) 100%)",
                }}
              >
                {/* Grain on layer */}
                <div
                  className="absolute inset-0 pointer-events-none opacity-[0.18] mix-blend-overlay z-10"
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
                    backgroundRepeat: "repeat",
                    backgroundSize: "180px",
                  }}
                />
                {/* Top edge highlight */}
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent z-10" />
                <div className="absolute inset-0 flex flex-col gap-4 p-8">
                  <p className="mb-2 text-[20px] font-light text-white/90">
                    Symbolica
                  </p>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-3 rounded border border-white/[0.06] bg-white/[0.03] px-3 py-2">
                      <div className="h-2 w-2 rounded-full bg-[#10a37f]"></div>
                      <p className="font-mono text-[12px] text-white/70">
                        GPT-5.1
                      </p>
                    </div>
                    <div className="flex items-center gap-3 rounded border border-white/[0.06] bg-white/[0.03] px-3 py-2">
                      <div className="h-2 w-2 rounded-full bg-[#d97706]"></div>
                      <p className="font-mono text-[12px] text-white/70">
                        Claude Opus 4.5
                      </p>
                    </div>
                    <div className="flex items-center gap-3 rounded border border-white/[0.06] bg-white/[0.03] px-3 py-2">
                      <div className="h-2 w-2 rounded-full bg-[#3b82f6]"></div>
                      <p className="font-mono text-[12px] text-white/70">
                        Gemini 3 Pro
                      </p>
                    </div>
                    <div className="flex items-center gap-3 rounded border border-white/[0.06] bg-white/[0.03] px-3 py-2">
                      <div className="h-2 w-2 rounded-full bg-[#8b5cf6]"></div>
                      <p className="font-mono text-[12px] text-white/70">
                        Opus 4.5
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Top Layer - Agentica */}
              <div
                className="absolute h-[300px] w-[450px] rounded-lg border backdrop-blur-sm transition-all duration-500 overflow-hidden"
                style={{
                  transform:
                    activeTab === "agentica"
                      ? "rotateX(60deg) rotateZ(-45deg) translateZ(80px) scale(1.03)"
                      : "rotateX(60deg) rotateZ(-45deg) translateZ(80px)",
                  transformStyle: "preserve-3d",
                  left: "50%",
                  top: "40%",
                  marginLeft: "-225px",
                  marginTop: "-150px",
                  borderColor:
                    activeTab === "agentica"
                      ? "rgba(255,255,255,0.25)"
                      : "rgba(255,255,255,0.08)",
                  boxShadow:
                    activeTab === "agentica"
                      ? "0 8px 40px rgba(0,0,0,0.6), 0 0 1px rgba(255,255,255,0.1)"
                      : "0 4px 20px rgba(0,0,0,0.4)",
                  background:
                    activeTab === "agentica"
                      ? "linear-gradient(145deg, rgba(30,30,35,0.95) 0%, rgba(15,15,18,0.95) 100%)"
                      : "linear-gradient(145deg, rgba(22,22,26,0.9) 0%, rgba(10,10,12,0.9) 100%)",
                }}
              >
                {/* Grain on layer */}
                <div
                  className="absolute inset-0 pointer-events-none opacity-[0.18] mix-blend-overlay z-10"
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
                    backgroundRepeat: "repeat",
                    backgroundSize: "180px",
                  }}
                />
                {/* Top edge highlight */}
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent z-10" />
                <div className="absolute inset-0 flex flex-col p-8">
                  <p className="mb-4 text-[20px] font-light text-white/90">
                    Agentica
                  </p>
                  <div className="font-mono text-[11px] leading-[1.6] text-white/60">
                    <div className="mb-1">
                      market_data ={" "}
                      <span className="text-[#5194c9]/80">TDClient</span>
                      <span className="text-[#eccc26]/80">(</span>apikey = os.
                      <span className="text-[#5194c9]/80">getenv</span>
                      <span className="text-[#eccc26]/80">(</span>
                      <span className="text-[#53d2be]/80">
                        &quot;TWELVE_DATA_API_KEY&quot;
                      </span>
                      <span className="text-[#eccc26]/80">))</span>
                    </div>
                    <div className="mb-3"></div>
                    <div className="mb-1">
                      data_agent ={" "}
                      <span className="text-[#c58ab8]/80">await</span>{" "}
                      <span className="text-[#5194c9]/80">spawn</span>
                      <span className="text-[#eccc26]/80">(</span>
                    </div>
                    <div className="mb-1 pl-4">
                      premise=
                      <span className="text-[#53d2be]/80">
                        &quot;You are a financial data analyst.&quot;
                      </span>
                      ,
                    </div>
                    <div className="mb-1 pl-4">
                      model=
                      <span className="text-[#53d2be]/80">
                        &quot;anthropic/claude-opus-4.5&quot;
                      </span>
                      ,
                    </div>
                    <div className="mb-1 pl-4">
                      scope=<span className="text-[#eccc26]/80">{"{"}</span>
                      <span className="text-[#53d2be]/80">
                        &apos;market_data&apos;
                      </span>
                      : market_data
                      <span className="text-[#eccc26]/80">{"}"}</span>
                    </div>
                    <div className="mb-1">
                      <span className="text-[#eccc26]/80">)</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features List (Right Side) - Stacked with gap */}
        <div className="flex h-auto flex-shrink-0 flex-col gap-4 lg:h-[500px] lg:flex-1">
          {layers.map((layer, index) => (
            <div
              key={layer.id}
              onMouseEnter={() =>
                setHoveredTab(layer.id as "agentica" | "symbolica")
              }
              onMouseLeave={() => setHoveredTab(null)}
              className={cn(
                "rounded-lg border border-[rgba(255,255,255,0.14)] p-8 transition-all duration-300",
                index === 0 ? "shrink-0" : "flex-1"
              )}
            >
              <h3 className="text-[25px] leading-[43.2px] tracking-[-0.9px] text-white text-pretty mb-4">
                {layer.title}
              </h3>
              <div className="font-arizona-sans text-[16px] font-light leading-[23px] text-white">
                {layer.description}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
